import type { CompanyColor } from '@ottd/api';

import { Hint } from '@/components/prototype/hint';
import { tr } from '@/lib/locale';
import { cn } from '@/lib/utils';

/*
 * A company's president portrait - the legacy site's OpenTTD manager face,
 * one per company colour (0 dark blue … 15 white). Each JPG already carries
 * that company colour as its background, so the tile reads as "this colour's
 * company" at a glance, and a colour strip underneath repeats it in the exact
 * in-game shade for the small sizes where the portrait background is subtle.
 */

const FACE_COUNT = 16;

/** OpenTTD's 16 company colours (company_gui palette), by colour index. */
/*
 * Lifted towards the portrait backgrounds (sampled from /faces/face_m_*.jpg)
 * so a swatch next to a face reads as the same colour on the dark panels; the
 * raw in-game palette's dark blue / dark green / purple sink into the theme.
 */
const COMPANY_COLORS: readonly string[] = [
  '#4a78b0', // 0 dark blue
  '#8ccc4c', // 1 pale green
  '#d47ea0', // 2 pink
  '#f0c832', // 3 yellow
  '#e83a34', // 4 red
  '#62b4e8', // 5 light blue
  '#5cb444', // 6 green
  '#3c8c3c', // 7 dark green
  '#5693cc', // 8 blue
  '#e8d0a8', // 9 cream
  '#b090c0', // 10 mauve
  '#9270d5', // 11 purple
  '#f0a848', // 12 orange
  '#b08860', // 13 brown
  '#9c9c9c', // 14 grey
  '#f0f0f0', // 15 white
];

/** Hex for a company colour index (clamped to the 16 OpenTTD colours). */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper colocated with the face it colours.
export function companyColorHex(color: CompanyColor): string {
  return COMPANY_COLORS[clampColor(color)];
}

function clampColor(color: CompanyColor): number {
  if (!Number.isFinite(color)) return 0;
  return Math.min(FACE_COUNT - 1, Math.max(0, Math.trunc(color)));
}

// Portraits are 92×119 - keep that aspect at every size.
const FACE_ASPECT = 119 / 92;

interface PresidentFaceProps {
  color: CompanyColor;
  /** Company name - the accessible label. */
  name: string;
  /** Rendered width in px; height follows the portrait aspect. */
  width?: number;
  /** Show the company-colour strip under the portrait (default on). */
  strip?: boolean;
  className?: string;
}

export function PresidentFace({
  color,
  name,
  width = 64,
  strip = true,
  className,
}: PresidentFaceProps) {
  const index = clampColor(color);
  return (
    <Hint label={name}>
      <span
        className={cn('inline-flex shrink-0 flex-col', className)}
        role="img"
        aria-label={tr('common:president', { company: name })}
      >
        <img
          src={`/faces/face_m_${index}.jpg`}
          alt=""
          width={width}
          height={Math.round(width * FACE_ASPECT)}
          className="bevel block"
          style={{ imageRendering: 'pixelated' }}
          draggable={false}
        />
        {strip ? (
          <span
            className="block h-1 border border-t-0 border-black"
            style={{ background: COMPANY_COLORS[index] }}
            aria-hidden
          />
        ) : null}
      </span>
    </Hint>
  );
}
